// Chip-Input für kompakte Listen-Werte.
// Tippen + Enter (oder Komma) erzeugt einen Chip, Backspace im leeren Feld
// entfernt den letzten, × am Chip entfernt gezielt. Einfügen von
// "a, b, c" wird in einzelne Chips zerlegt. Die Chips sind reine UI —
// wie die Werte serialisiert werden (z.B. Komma-getrennt für Amazon),
// entscheidet weiterhin der Server/Export.

import { useState } from "react";
import { useTranslation } from "../../i18n";

interface ChipsInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  name?: string;
  ariaLabel?: string;
  variant?: "default" | "quiet";
}

export default function ChipsInput({
  value,
  onChange,
  placeholder,
  disabled,
  id,
  name,
  ariaLabel,
  variant = "default",
}: ChipsInputProps) {
  const { t: translate } = useTranslation();
  const tf = (key: string, fallback: string) => {
    const translated = translate(key);
    return typeof translated === "string" && translated !== key ? translated : fallback;
  };
  const [draft, setDraft] = useState("");

  const commit = (raw: string) => {
    const parts = raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .filter((s) => !value.includes(s));
    if (parts.length > 0) onChange([...value, ...parts]);
    setDraft("");
  };

  return (
    <div
      className={`flex w-full flex-wrap items-center gap-1.5 transition-colors ${
        variant === "quiet"
          ? "min-h-9 border-b border-outline bg-transparent px-0.5 py-1.5 focus-within:border-primary"
          : "rounded-lg border border-outline bg-surface-container-low px-2.5 py-1.5 focus-within:ring-2 focus-within:ring-primary/40"
      } ${
        disabled ? "opacity-60" : ""
      }`}
    >
      {value.map((chip) => (
        <span
          key={chip}
          className="inline-flex max-w-full items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-xs text-on-surface"
        >
          <span className="truncate">{chip}</span>
          <button
            type="button"
            className="rounded-sm text-on-surface-variant hover:text-error leading-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            onClick={() => onChange(value.filter((c) => c !== chip))}
            disabled={disabled}
            aria-label={tf("common.removeNamed", "{{name}} entfernen").replace("{{name}}", chip)}
          >
            ×
          </button>
        </span>
      ))}
      <input
        id={id}
        name={name || id || "chips-input"}
        autoComplete="off"
        aria-label={ariaLabel || tf("common.chipsInputLabel", "Listeneintrag hinzufügen")}
        value={draft}
        disabled={disabled}
        placeholder={value.length === 0 ? placeholder : undefined}
        onChange={(e) => {
          // Komma im Tippfluss schließt den Chip sofort ab
          if (e.target.value.includes(",")) commit(e.target.value);
          else setDraft(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit(draft);
          } else if (e.key === "Backspace" && draft === "" && value.length > 0) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={() => {
          if (draft.trim()) commit(draft);
        }}
        className="min-w-[120px] flex-1 appearance-none border-0 bg-transparent py-0.5 text-sm text-on-surface shadow-none outline-none"
      />
    </div>
  );
}
